<script setup lang="ts">
/**
 * Bouton PayPal Smart Buttons à afficher EN ALTERNATIVE du checkout Stripe
 * principal sur les pages de don. Utilisé quand l'asso a configuré Stripe
 * ET PayPal — Stripe gère Apple Pay/Google Pay/CardFields, PayPal gère
 * uniquement son propre bouton (Connect PayPal account).
 *
 * Pourquoi ce split :
 *   - Stripe wallets supportent le récurrent ; PayPal wallets non
 *   - Stripe CardFields est plus mature et localisé FR
 *   - PayPal Button capture la clientèle PayPal-native (~10-15% des donateurs FR)
 *
 * Cf. memory/paypal_integration_plan.md §6.2 (scénario B)
 */

import {
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
  type ComputedRef,
  type Ref,
} from "vue";
import type { useCheckout } from "@donoor/checkout";
import { logger } from "~/utils/logger";

/**
 * Vue minimale du composable `usePaymentForm` consommée par ce bouton :
 * on n'utilise QUE `isSubscription` (lu dans le watch) — on évite donc
 * `paymentForm: any` sans tout typer.
 */
interface PaymentFormSlice {
  isSubscription: Ref<boolean> | ComputedRef<boolean>;
}

interface Props {
  paypalCheckout: ReturnType<typeof useCheckout>;
  /** Composable de form Donoor (`usePaymentForm()`) — uniquement `isSubscription`. */
  paymentForm: PaymentFormSlice;
  /** ID unique de container — utile si on a plusieurs instances sur la page */
  containerId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  containerId: "paypal-alt-buttons-container",
});

const { t } = useI18n();

const paypalBtnReady = computed(
  () => props.paypalCheckout.expressCheckoutReady.value,
);
// On ne déclare le bouton "indisponible" QUE si le bouton lui-même a planté
// (init SDK, ou éligibilité Buttons). Une erreur sur CardFields (ex:
// PAYPAL_CARDFIELDS_NOT_ELIGIBLE quand le sandbox n'a pas la capability)
// ne doit PAS contaminer le statut du bouton — les 2 sont indépendants.
// `expressCheckoutError` = échec du BOUTON seul. L'ancienne source (`error`)
// mélangeait carte, wallets et soumission : un échec de montage de la carte
// affichait le bouton PayPal « indisponible » (événement gratuit + pourboire,
// prod 2026-09-25).
const paypalInitError = computed(() => {
  const err = props.paypalCheckout.expressCheckoutError.value;
  if (!err) return false;
  // Ignore les erreurs liées à CardFields qui n'affectent pas le bouton.
  if (err.code === "PAYPAL_CARDFIELDS_NOT_ELIGIBLE") return false;
  // Ignore les erreurs runtime (capture refusée, mock decline, etc.) :
  // ce sont des refus de paiement spécifiques, pas une indisponibilité du
  // SDK. Elles sont déjà affichées sur le bloc carte (bordure rouge +
  // message discret). Sinon on aurait DOUBLE affichage. Karim 2026-06-28.
  if (err.type === "card_error") return false;
  return true;
});

async function mountPaypalButtons() {
  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await props.paypalCheckout.mountExpressCheckout(props.containerId);
  } catch (err) {
    logger.warn("[PayPalAlternativeButton] mount failed:", err);
  }
}

onMounted(() => {
  mountPaypalButtons();
});

// Démonte proprement le bouton PayPal au unmount du composant : sinon, au
// retour sur la page, l'ancienne instance reste dans le DOM et le nouveau
// montage AJOUTE un bouton → boutons en double. Karim 2026-06-23.
// ⚠️ `destroyExpressCheckout`, PAS `destroy` : l'instance PayPal est PARTAGÉE
// avec la carte PayPal et les wallets de la page. `destroy()` les éteignait
// tous dès que ce bouton sortait de l'écran (2026-09-25).
onBeforeUnmount(() => {
  props.paypalCheckout.destroyExpressCheckout();
});

// Re-mount au changement de mode récurrent (les Smart Buttons PayPal ne sont
// pas modifiables après mount — createSubscription vs createOrder en interne).
watch(
  () => props.paymentForm.isSubscription.value,
  () => {
    props.paypalCheckout.destroyExpressCheckout();
    mountPaypalButtons();
  },
);
</script>

<template>
  <div class="paypal-alt-block mt-2">
    <!-- Erreur init du SDK PayPal — affichage discret, ne bloque pas le flow Stripe -->
    <div
      v-if="paypalInitError"
      class="text-xs text-gray-500 text-center py-2"
    >
      {{ t('don.paypalAlternatif.indisponible') }}
    </div>

    <!--
      Skeleton + container superposés. Le container DOIT toujours être dans
      le DOM (pas de `v-if`) pour que le SDK PayPal puisse y monter ses
      iframes sandboxées. Avant ce fix, le skeleton était `v-else-if` du
      message d'erreur : il restait visible TANT que `paypalBtnReady` était
      false, mais le container PayPal en dessous était lui-aussi visible
      (v-show) → les deux coexistaient quand le SDK montait son bouton.

      Désormais on les empile en `relative + absolute inset-0` et on toggle
      le skeleton via `v-show="!paypalBtnReady"`. Quand `paypalBtnReady`
      passe à true, le skeleton disparaît immédiatement, le bouton PayPal
      en dessous prend le relais. Zéro flash, zéro coexistence.
    -->
    <!-- `v-show`, PAS `v-else` : le conteneur reste dans la page même en
         cas d'erreur. Avec `v-else`, il disparaissait — plus aucun
         remontage ne pouvait réussir (2026-09-25). -->
    <div
      v-show="!paypalInitError"
      class="relative min-h-[44px] w-full"
    >
      <div
        v-show="!paypalBtnReady"
        class="paypal-alt-skeleton absolute inset-x-0 top-0 mx-auto h-[44px] w-full max-w-[750px] rounded-[6px]"
        aria-hidden="true"
      />
      <!-- Container FULL-WIDTH : le SDK PayPal en layout `horizontal`
           (cf. PaypalProvider.ts) étire son bouton à 100% de ce parent,
           donc le bouton fait la même largeur que les autres blocs du
           formulaire. PayPal borne lui-même à 750px max en interne, ce qui
           reste > à la colonne du form → toujours full-width en pratique.
           Karim 2026-07-05. -->
      <div
        :id="containerId"
        class="paypal-alt-container"
        style="width: 100%; min-height: 44px;"
      />
    </div>
  </div>
</template>

<style scoped>
/* SDK PayPal Multiparty en layout `horizontal` (cf. PaypalProvider.ts) :
   le bouton s'étire à 100% de SON parent direct. On donne donc à ce
   container 100% de largeur → bouton FULL-WIDTH aligné sur les autres
   blocs du formulaire (Karim 2026-07-05, remplace l'ancien hack 300px
   centré du layout vertical). */
.paypal-alt-container {
  width: 100%;
  min-height: 44px;
  /* Le SDK PayPal (layout `horizontal`) rend un wrapper `.paypal-buttons`
     qui, en full-width, laisse son logo aligné à GAUCHE de la barre jaune.
     On passe le container en flex centré pour recentrer horizontalement le
     bouton (et donc son logo) dans la colonne. Karim 2026-07-08. */
  display: flex;
  justify-content: center;
}

/* Le wrapper SDK doit garder 100% pour que la barre jaune reste full-width :
   c'est le CONTENU (logo) qui est recentré par le flex ci-dessus. */
.paypal-alt-container :deep(.paypal-buttons) {
  width: 100% !important;
  margin: 0 auto !important;
}

/* 🔴 PayPal borne son bouton à 750 px, À L'INTÉRIEUR de son iframe. Une
   colonne plus large le laissait COLLÉ À GAUCHE, tout le reste du bloc étant
   pleine largeur (Karim 2026-09-26, récap boutique). On borne donc le cadre
   du SDK à la même largeur : le `margin: auto` ci-dessus peut alors le
   centrer. Sous 750 px, rien ne change (toujours pleine largeur). */
.paypal-alt-container :deep(.paypal-buttons) {
  max-width: 750px !important;
}

/* Masque le tagline + le bouton "Powered by PayPal" sous le bouton. */
.paypal-alt-container :deep(.paypal-button-tagline),
.paypal-alt-container :deep([data-funding-source="paypal"] + div),
.paypal-alt-container :deep(.paypal-powered-by) {
  display: none !important;
}

/* Skeleton aux couleurs PayPal (gold #FFC439) avec un sweep horizontal
   subtil — préférable à un pulse brutal sur un fond coloré. */
.paypal-alt-skeleton {
  background: linear-gradient(
    90deg,
    #ffc439 0%,
    #ffd569 50%,
    #ffc439 100%
  );
  background-size: 200% 100%;
  animation: paypal-alt-shimmer 1.6s ease-in-out infinite;
}
@keyframes paypal-alt-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
